import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Kosa } from '../../../types/kosa';
import KosaDepthsStore from '../../../stores/kosa-depths-store';

//=================================================================================================
interface Props {
    kosa: Kosa | null;
    onClose: () => void;
}

function KosaSettingsDialog({ kosa, onClose }: Props) {
    const [store] = useState(() => new KosaDepthsStore(kosa?.id ?? 0));
    const [loadError, setLoadError] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [depths, setDepths] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [factoryError, setFactoryError] = useState(false);
    const [factoryNotFound, setFactoryNotFound] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (!kosa) return;
        store
            .load()
            .catch(() => setLoadError(true))
            .finally(() => setLoaded(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!loaded || !kosa) return;
        if (store.depths.length > 0) {
            setDepths(store.depths.map((d) => String(d / 100)));
        } else {
            setDepths(Array.from({ length: kosa.sensorCount }, (_, i) => String(i)));
        }
    }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDepthChange = (index: number, value: string) => {
        setDepths((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const parsed = depths.map((d) => parseFloat(d));

    const fieldError = (i: number): string | null => {
        if (depths[i] === '' || isNaN(parsed[i])) return 'Введите значение';
        if (i > 0 && parsed[i] < parsed[i - 1]) return 'Глубина не может быть меньше предыдущей';
        return null;
    };

    const hasInvalidInput = depths.some((d) => d === '' || isNaN(parseFloat(d)));

    const handleLoadFromFactory = async () => {
        setFactoryError(false);
        setFactoryNotFound(false);
        try {
            const cumulative = await store.fromFactory();
            if (cumulative.length === 0) {
                setFactoryNotFound(true);
            } else {
                setDepths(cumulative.map((d) => String(d / 100)));
            }
        } catch {
            setFactoryError(true);
        }
    };

    const handleSave = async () => {
        if (!kosa || hasInvalidInput) return;
        setSaving(true);
        try {
            await store.save(parsed.map((d, i, arr) => Math.round((d - (arr[i - 1] ?? 0)) * 100)));
            onClose();
        } catch {
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    if (!kosa) return null;

    const isInitialLoading = !loaded;
    const isLoading = store.loading || !loaded;

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Настройки Коса-{kosa.id}</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {isInitialLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : loadError ? (
                    <Alert severity="error" sx={{ m: 2 }}>
                        Ошибка загрузки данных
                    </Alert>
                ) : (
                    <Box sx={{ position: 'relative' }}>
                        {(store.loading || saving) && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        <TableContainer sx={{ maxHeight: 420 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 60 }}>№</TableCell>
                                        <TableCell>Глубина, м</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {depths.map((d, i) => {
                                        const err = fieldError(i);
                                        return (
                                            <TableRow key={i}>
                                                <TableCell>{i + 1}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={d}
                                                        error={err !== null}
                                                        onChange={(e) => handleDepthChange(i, e.target.value)}
                                                        slotProps={{
                                                            htmlInput: {
                                                                step: 0.1,
                                                                min: 0,
                                                                style: { padding: '2px 6px' },
                                                            },
                                                            input: err
                                                                ? {
                                                                      endAdornment: (
                                                                          <InputAdornment position="end">
                                                                              <Tooltip title={err}>
                                                                                  <WarningAmberIcon
                                                                                      color="error"
                                                                                      sx={{ fontSize: 16 }}
                                                                                  />
                                                                              </Tooltip>
                                                                          </InputAdornment>
                                                                      ),
                                                                  }
                                                                : undefined,
                                                        }}
                                                        sx={{ width: 120 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <Collapse in={saveError}>
                <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                    Ошибка сохранения
                </Alert>
            </Collapse>
            <Collapse in={factoryError}>
                <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                    Ошибка загрузки данных от производителя
                </Alert>
            </Collapse>
            <Collapse in={factoryNotFound}>
                <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
                    Данные от производителя не найдены
                </Alert>
            </Collapse>
            <DialogActions>
                <Button
                    sx={{ mr: 'auto' }}
                    onClick={handleLoadFromFactory}
                    disabled={isLoading || saving}
                >
                    Подтянуть значения
                </Button>
                <Button onClick={onClose} disabled={saving || store.loading}>
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isLoading || saving || loadError || hasInvalidInput}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default observer(KosaSettingsDialog);
